import React, { useEffect, useRef } from 'react'

interface IObserverProps {
  selector: string
  selectedTab: number
  threshold?: number
  onVisible: (id: number) => void
  tabs: {
    label: string
  }[]
}

const FormWrapperTabVisibilityObserver: React.FC<IObserverProps> = ({
  selector,
  selectedTab,
  threshold = 0.3,
  onVisible,
  tabs
}) => {
  const callbackRef = useRef(onVisible)
  callbackRef.current = onVisible

  const rafRef = useRef<number | null>(null)
  const visibilityMap = useRef<Map<number, number>>(new Map())

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = Number(entry.target.getAttribute('data-watch-id'))
          if (!isNaN(id)) {
            if (entry.isIntersecting) {
              visibilityMap.current.set(id, entry.intersectionRatio)
            } else {
              visibilityMap.current.delete(id)
            }
          }
        })

        if (rafRef.current) cancelAnimationFrame(rafRef.current)

        rafRef.current = requestAnimationFrame(() => {
          const visible = Array.from(visibilityMap.current.entries())
          if (visible.length === 0) return

          visible.sort((a, b) => b[0] - a[0])
          const [winnerId] = visible[0]

          if (winnerId !== selectedTab) {
            callbackRef.current(winnerId)
          }
        })
      },
      { threshold }
    )

    // Poll for elements until they exist
    let interval: ReturnType<typeof setInterval> | null = null
    const startObserving = () => {
      const elements = document.querySelectorAll(selector)
      if (elements.length > 0) {
        elements.forEach((el) => observer.observe(el))
        if (interval) clearInterval(interval)
      }
    }

    startObserving()
    interval = setInterval(startObserving, 50) // keep checking until elements exist

    return () => {
      observer.disconnect()
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      if (interval) clearInterval(interval)
    }
  }, [selector, threshold, selectedTab, tabs])

  return null
}

export default FormWrapperTabVisibilityObserver
